/**
 * Page /abonnement — catalogue des tiers d'abonnement webtarot.
 *
 * Comportement :
 * - Sur web : boutons "S'abonner" / "Essayer 15 jours" qui redirigent vers Stripe Checkout
 * - Sur natif Capacitor (reader app) : pas de bouton d'achat, juste un texte
 *   informatif pointant vers webtarot.fr (conformité App Store / Play Store)
 *
 * Données du catalogue récupérées via `/api/billing/catalog`. Le contenu
 * textuel des features est local (statique) — il est dépendant des
 * décisions de Phase 0 (docs/billing/00_business_model.md) et donc plus
 * stable qu'une réponse serveur.
 */

import { useEffect, useState } from 'react';
import { useApp } from '../contexts/AppContext';
import { useSubscription } from '../hooks/useSubscription';
import {
  fetchCatalog,
  createCheckout,
  type BillingCatalog,
  type BillingTier,
  type BillingPeriod,
} from '../lib/billingApi';
import { isNative, openExternalUrl } from '../platform/capacitor';
import type { Session } from '../types/app';
import '../styles/subscription.css';

interface SubscriptionProps {
  session: Session;
}

interface TierUI {
  tier: BillingTier;
  name: string;
  recommended?: boolean;
  features: Array<{ label: string; included: boolean }>;
  pricesEur: Partial<Record<BillingPeriod, number>>;
  showTrial: boolean;
}

const TIERS_UI: TierUI[] = [
  {
    tier: 'free',
    name: 'Détente — Gratuit',
    features: [
      { label: 'Parties contre IA illimitées', included: true },
      { label: 'Chat global et forums', included: true },
      { label: 'Classements détente', included: true },
      { label: 'Bibliothèque de parties (5 max)', included: true },
      { label: 'Coach IA d\'analyse', included: false },
      { label: 'Espace compétition', included: false },
    ],
    pricesEur: { yearly: 0, monthly: 0 },
    showTrial: false,
  },
  {
    tier: 'competition',
    name: 'Espace Compétition',
    recommended: true,
    features: [
      { label: 'Tout l\'espace détente', included: true },
      { label: 'Tournois différés', included: true },
      { label: 'Championnat individuel CIA', included: true },
      { label: 'Championnat par équipes', included: true },
      { label: 'Coach IA d\'analyse illimité', included: true },
      { label: 'Bibliothèque illimitée', included: true },
    ],
    pricesEur: { yearly: 25, monthly: 3 },
    showTrial: true,
  },
];

function formatPrice(amount: number, period: BillingPeriod): string {
  if (amount === 0) return 'Gratuit';
  const periodLabel = period === 'yearly' ? '/an' : '/mois';
  return `${amount.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR', minimumFractionDigits: 0 })}${periodLabel}`;
}

export default function Subscription({ session }: SubscriptionProps) {
  const { setActiveTab } = useApp();
  const { subscription, isTrial, daysUntilExpiry } = useSubscription(session.pseudo, session.token);
  const [catalog, setCatalog] = useState<BillingCatalog | null>(null);
  const [selectedPeriod, setSelectedPeriod] = useState<Record<BillingTier, BillingPeriod>>({
    free: 'yearly',
    classic_plus: 'yearly',
    competition: 'yearly',
  });
  const [loadingTier, setLoadingTier] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  useEffect(() => {
    fetchCatalog().then(setCatalog).catch(() => {
      // Catalogue serveur non dispo : on garde l'affichage statique
    });
  }, []);

  const native = isNative();
  const trialDays = catalog?.trialDays ?? 15;

  const handleSubscribe = async (tier: BillingTier, withTrial: boolean) => {
    if (!session.token) {
      setErrorMsg('Vous devez être connecté pour vous abonner.');
      return;
    }
    setLoadingTier(`${tier}-${withTrial ? 'trial' : 'paid'}`);
    setErrorMsg(null);

    try {
      const { url } = await createCheckout(session.token, {
        tier,
        billing: selectedPeriod[tier],
        withTrial,
        ...(withTrial
          ? { identityKey: `email:${session.pseudo}@webtarot.fr`, identityType: 'email' }
          : {}),
      });
      if (native) {
        await openExternalUrl(url);
      } else {
        window.location.href = url;
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      if (msg === 'trial_already_used') {
        setErrorMsg('Un essai gratuit a déjà été utilisé sur ce compte.');
      } else {
        setErrorMsg(`Impossible de lancer le paiement : ${msg}`);
      }
    } finally {
      setLoadingTier(null);
    }
  };

  return (
    <div className="subscription-page">
      <header className="subscription-header">
        <h1 className="subscription-title">Choisissez votre abonnement</h1>
        <p className="subscription-subtitle">
          Profitez de webtarot dans toute sa profondeur — résiliable à tout moment.
        </p>
      </header>

      {/* Bandeau statut courant */}
      {subscription && (
        <div className={`subscription-banner ${isTrial ? 'trial' : 'active'}`}>
          <div className="subscription-banner-text">
            {isTrial ? (
              <>
                <strong>Essai gratuit en cours</strong>
                <small>
                  Il vous reste {daysUntilExpiry} jour{daysUntilExpiry === 1 ? '' : 's'}.
                  Souscrivez pour conserver l'accès.
                </small>
              </>
            ) : (
              <>
                <strong>Abonnement {subscription.tier} actif</strong>
                <small>
                  Valable jusqu'au {new Date(subscription.currentPeriodEnd).toLocaleDateString('fr-FR')}.
                </small>
              </>
            )}
          </div>
          <button
            className="subscription-tier-cta"
            onClick={() => setActiveTab('mySubscription')}
            type="button"
            style={{ background: 'transparent', border: '1px solid var(--border-color)', color: 'var(--text-primary)', padding: '8px 16px', borderRadius: 8, cursor: 'pointer' }}
          >
            Gérer mon abonnement
          </button>
        </div>
      )}

      {/* Message d'erreur global */}
      {errorMsg && (
        <div className="subscription-banner warning">
          <div className="subscription-banner-text">
            <strong>Erreur</strong>
            <small>{errorMsg}</small>
          </div>
        </div>
      )}

      {/* Notice mode reader (native uniquement) */}
      {native && (
        <div className="subscription-reader-notice">
          <strong>Pour vous abonner depuis votre mobile</strong>,
          rendez-vous sur <strong>webtarot.fr</strong> depuis votre
          navigateur. L'application mobile sert à jouer, l'abonnement se gère sur le web.
        </div>
      )}

      {/* Grille des tiers */}
      <div className="subscription-tiers">
        {TIERS_UI.map((t) => (
          <div key={t.tier} className={`subscription-tier ${t.recommended ? 'recommended' : ''}`}>
            {t.recommended && <span className="subscription-tier-badge">Recommandé</span>}
            <h2 className="subscription-tier-name">{t.name}</h2>

            {!native && (
              <>
                <div className="subscription-tier-price">
                  <span className="subscription-tier-price-amount">
                    {t.pricesEur[selectedPeriod[t.tier]] !== undefined
                      ? formatPrice(t.pricesEur[selectedPeriod[t.tier]]!, selectedPeriod[t.tier])
                      : '—'}
                  </span>
                </div>

                {Object.keys(t.pricesEur).length > 1 && (
                  <div className="subscription-tier-billing-toggle">
                    {(['yearly', 'monthly'] as BillingPeriod[]).map((p) => (
                      t.pricesEur[p] !== undefined && (
                        <button
                          key={p}
                          type="button"
                          className={selectedPeriod[t.tier] === p ? 'active' : ''}
                          onClick={() => setSelectedPeriod(prev => ({ ...prev, [t.tier]: p }))}
                        >
                          {p === 'yearly' ? 'Annuel' : 'Mensuel'}
                        </button>
                      )
                    ))}
                  </div>
                )}
              </>
            )}

            <ul className="subscription-tier-features">
              {t.features.map((f, i) => (
                <li key={i} className={f.included ? '' : 'disabled'}>{f.label}</li>
              ))}
            </ul>

            {t.tier === 'free' ? (
              <div className="subscription-tier-foot">
                Compte courant — aucune action requise
              </div>
            ) : native ? (
              <div className="subscription-tier-cta">
                <button type="button" disabled>
                  Sur webtarot.fr
                </button>
                <div className="subscription-tier-foot">
                  Souscription disponible uniquement sur le site web.
                </div>
              </div>
            ) : (
              <div className="subscription-tier-cta">
                <button
                  type="button"
                  disabled={!!loadingTier}
                  onClick={() => handleSubscribe(t.tier, false)}
                >
                  {loadingTier === `${t.tier}-paid` ? '…' : 'S\'abonner'}
                </button>
                {t.showTrial && !subscription && (
                  <button
                    type="button"
                    className="secondary"
                    disabled={!!loadingTier}
                    onClick={() => handleSubscribe(t.tier, true)}
                  >
                    {loadingTier === `${t.tier}-trial` ? '…' : `Essayer ${trialDays} jours`}
                  </button>
                )}
                <div className="subscription-tier-foot">
                  Sans engagement. Résiliable à tout moment via votre compte.
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
